import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Clock, Users, ArrowRight } from 'lucide-react';

interface TeeTimeSlot {
  courseId: number;
  courseName: string;
  region: string;
  date: string;
  time: string;
  availableSpots: number;
  maxPlayers: number;
  price?: number;
}


const LiveTeeTimesScroller: React.FC = () => {
  const [teeTimeSlots, setTeeTimeSlots] = useState<TeeTimeSlot[]>([]);
  const [error, setError] = useState<string | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [isAutoScrolling, setIsAutoScrolling] = useState(true);

  // Get date 5 days from now
  const getFutureDateAfter5Days = () => {
    const futureDate = new Date();
    futureDate.setDate(futureDate.getDate() + 5);
    return futureDate.toISOString().split('T')[0];
  };

  const fetchTeeTimesData = useCallback(async () => {
    try {
      const futureDate = getFutureDateAfter5Days();

      // Mock tee time data - hardcoded for stability (availableSpots: 1-4 only)
      const mockTeeTimeSlots: TeeTimeSlot[] = [
        // Langara Golf Course
        { courseId: 1, courseName: 'Langara Golf Course', region: 'Vancouver, BC', date: futureDate, time: '8:30 AM', availableSpots: 2, maxPlayers: 4, price: 45 },
        { courseId: 1, courseName: 'Langara Golf Course', region: 'Vancouver, BC', date: futureDate, time: '9:15 AM', availableSpots: 3, maxPlayers: 4, price: 45 },
        { courseId: 1, courseName: 'Langara Golf Course', region: 'Vancouver, BC', date: futureDate, time: '2:30 PM', availableSpots: 1, maxPlayers: 4, price: 42 },
        
        // Fraserview Golf Course
        { courseId: 2, courseName: 'Fraserview Golf Course', region: 'Vancouver, BC', date: futureDate, time: '9:00 AM', availableSpots: 4, maxPlayers: 4, price: 48 },
        { courseId: 2, courseName: 'Fraserview Golf Course', region: 'Vancouver, BC', date: futureDate, time: '11:45 AM', availableSpots: 2, maxPlayers: 4, price: 48 },
        { courseId: 2, courseName: 'Fraserview Golf Course', region: 'Vancouver, BC', date: futureDate, time: '4:15 PM', availableSpots: 3, maxPlayers: 4, price: 44 },
        
        // McCleery Golf Course
        { courseId: 3, courseName: 'McCleery Golf Course', region: 'Vancouver, BC', date: futureDate, time: '10:30 AM', availableSpots: 2, maxPlayers: 4, price: 38 },
        { courseId: 3, courseName: 'McCleery Golf Course', region: 'Vancouver, BC', date: futureDate, time: '1:00 PM', availableSpots: 1, maxPlayers: 4, price: 35 },
        { courseId: 3, courseName: 'McCleery Golf Course', region: 'Vancouver, BC', date: futureDate, time: '3:45 PM', availableSpots: 4, maxPlayers: 4, price: 32 },
        
        // Burnaby Mountain Golf Course
        { courseId: 4, courseName: 'Burnaby Mountain Golf Course', region: 'Burnaby, BC', date: futureDate, time: '8:00 AM', availableSpots: 3, maxPlayers: 4, price: 52 },
        { courseId: 4, courseName: 'Burnaby Mountain Golf Course', region: 'Burnaby, BC', date: futureDate, time: '12:30 PM', availableSpots: 2, maxPlayers: 4, price: 52 },
        { courseId: 4, courseName: 'Burnaby Mountain Golf Course', region: 'Burnaby, BC', date: futureDate, time: '5:00 PM', availableSpots: 1, maxPlayers: 4, price: 48 },
        
        // Riverway Golf Course
        { courseId: 5, courseName: 'Riverway Golf Course', region: 'Burnaby, BC', date: futureDate, time: '9:30 AM', availableSpots: 4, maxPlayers: 4, price: 41 },
        { courseId: 5, courseName: 'Riverway Golf Course', region: 'Burnaby, BC', date: futureDate, time: '2:00 PM', availableSpots: 2, maxPlayers: 4, price: 38 },
        { courseId: 5, courseName: 'Riverway Golf Course', region: 'Burnaby, BC', date: futureDate, time: '6:15 PM', availableSpots: 3, maxPlayers: 4, price: 35 },
        
        // Country Meadows Golf Club
        { courseId: 6, courseName: 'Country Meadows Golf Club', region: 'Richmond, BC', date: futureDate, time: '8:45 AM', availableSpots: 1, maxPlayers: 4, price: 55 },
        { courseId: 6, courseName: 'Country Meadows Golf Club', region: 'Richmond, BC', date: futureDate, time: '11:00 AM', availableSpots: 3, maxPlayers: 4, price: 55 },
        { courseId: 6, courseName: 'Country Meadows Golf Club', region: 'Richmond, BC', date: futureDate, time: '4:30 PM', availableSpots: 2, maxPlayers: 4, price: 50 },
        
        // Savage Creek Golf Course
        { courseId: 7, courseName: 'Savage Creek Golf Course', region: 'Richmond, BC', date: futureDate, time: '10:00 AM', availableSpots: 4, maxPlayers: 4, price: 58 },
        { courseId: 7, courseName: 'Savage Creek Golf Course', region: 'Richmond, BC', date: futureDate, time: '1:30 PM', availableSpots: 1, maxPlayers: 4, price: 55 },
        { courseId: 7, courseName: 'Savage Creek Golf Course', region: 'Richmond, BC', date: futureDate, time: '5:45 PM', availableSpots: 2, maxPlayers: 4, price: 52 },
        
        // Mylora Golf Club
        { courseId: 8, courseName: 'Mylora Golf Club', region: 'Richmond, BC', date: futureDate, time: '9:45 AM', availableSpots: 3, maxPlayers: 4, price: 42 },
        { courseId: 8, courseName: 'Mylora Golf Club', region: 'Richmond, BC', date: futureDate, time: '12:00 PM', availableSpots: 2, maxPlayers: 4, price: 40 },
        { courseId: 8, courseName: 'Mylora Golf Club', region: 'Richmond, BC', date: futureDate, time: '3:15 PM', availableSpots: 4, maxPlayers: 4, price: 38 },
        
        // UBC Golf Club
        { courseId: 9, courseName: 'UBC Golf Club', region: 'Vancouver, BC', date: futureDate, time: '8:15 AM', availableSpots: 2, maxPlayers: 4, price: 65 },
        { courseId: 9, courseName: 'UBC Golf Club', region: 'Vancouver, BC', date: futureDate, time: '11:30 AM', availableSpots: 1, maxPlayers: 4, price: 65 },
        { courseId: 9, courseName: 'UBC Golf Club', region: 'Vancouver, BC', date: futureDate, time: '4:00 PM', availableSpots: 3, maxPlayers: 4, price: 60 }
      ];

      // Set the tee time slots (shuffle for variety)
      const shuffledSlots = mockTeeTimeSlots.sort(() => Math.random() - 0.5);
      setTeeTimeSlots(shuffledSlots);
      setError(null);
    } catch (err) {
      console.error('Error setting mock tee times:', err);
      setError('Unable to load tee times');
      setTeeTimeSlots([]);
    }
  }, []);

  useEffect(() => {
    fetchTeeTimesData();
  }, [fetchTeeTimesData]);

  // Auto scroll functionality with continuous smooth scrolling
  useEffect(() => {
    if (!isAutoScrolling || !scrollContainerRef.current || teeTimeSlots.length === 0) return;

    const scrollContainer = scrollContainerRef.current;
    const cardWidth = 288; // w-72 = 288px
    const cardGap = 16; // space-x-4 = 16px
    const cardTotalWidth = cardWidth + cardGap;
    const totalCards = teeTimeSlots.length;
    const oneSetWidth = totalCards * cardTotalWidth;
    
    let animationId: number;
    const scrollSpeed = 0.5; // pixels per frame
    
    const animate = () => {
      if (scrollContainer && isAutoScrolling) {
        const currentScroll = scrollContainer.scrollLeft;
        
        // If we've scrolled past the first set, reset to beginning
        if (currentScroll >= oneSetWidth) {
          scrollContainer.scrollLeft = 0;
        } else {
          // Continuous smooth scroll
          scrollContainer.scrollLeft += scrollSpeed;
        }
        
        animationId = requestAnimationFrame(animate);
      }
    };
    
    animationId = requestAnimationFrame(animate);

    return () => {
      if (animationId) {
        cancelAnimationFrame(animationId);
      }
    };
  }, [isAutoScrolling, teeTimeSlots]);



  // Format date for display
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="w-full">

      {/* Scrollable Cards Container */}
      <div
        ref={scrollContainerRef}
        className="flex space-x-4 overflow-x-auto scrollbar-hide pb-4"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        onMouseEnter={() => setIsAutoScrolling(false)}
        onMouseLeave={() => setIsAutoScrolling(true)}
      >
        {/* Individual tee time slot cards */}
        {teeTimeSlots.map((slot, index) => (
          <Link
            key={`${slot.courseId}-${slot.time}-${index}`}
            to="/tee-times"
            className={`flex-none w-72 bg-white rounded-xl border border-green-100 shadow-md hover:shadow-lg transition-all duration-300 hover:scale-105 cursor-pointer ${
              index === 0 ? 'ml-4 md:ml-6' : ''
            } ${index === teeTimeSlots.length - 1 ? 'mr-4 md:mr-6' : ''}`}
          >
            <div className="p-4">
              {/* Course Name */}
              <div className="mb-3">
                <h3 className="text-sm font-semibold text-gray-900 truncate">
                  🏌️ {slot.courseName}
                </h3>
                <div className="flex items-center text-xs text-gray-500 mt-1">
                  <MapPin size={10} className="mr-1" />
                  {slot.region.split(',')[0]}
                </div>
              </div>

              {/* Date & Time */}
              <div className="mb-3">
                <div className="flex items-center text-xs font-medium text-gray-600 mb-1">
                  <Clock size={10} className="mr-1" />
                  {formatDate(slot.date)}
                </div>
                <div className="text-lg font-bold text-green-600">
                  {slot.time}
                </div>
              </div>

              {/* Available Spots */}
              <div className="flex items-center justify-between">
                <div className="flex items-center text-xs text-green-600">
                  <Users size={12} className="mr-1" />
                  {slot.availableSpots === slot.maxPlayers
                    ? `${slot.maxPlayers} spots`
                    : `${slot.availableSpots}-${slot.maxPlayers} spots`
                  }
                </div>
                {slot.price && (
                  <div className="text-xs font-semibold text-gray-800">
                    ${slot.price}
                  </div>
                )}
              </div>
            </div>
          </Link>
        ))}
        
        {/* Duplicate set for seamless infinite scroll */}
        {teeTimeSlots.map((slot, index) => (
          <Link
            key={`${slot.courseId}-${slot.time}-${index}-dup`}
            to="/tee-times"
            className="flex-none w-72 bg-white rounded-xl border border-green-100 shadow-md hover:shadow-lg transition-all duration-300 hover:scale-105 cursor-pointer"
          >
            <div className="p-4">
              {/* Course Name */}
              <div className="mb-3">
                <h3 className="text-sm font-semibold text-gray-900 truncate">
                  🏌️ {slot.courseName}
                </h3>
                <div className="flex items-center text-xs text-gray-500 mt-1">
                  <MapPin size={10} className="mr-1" />
                  {slot.region.split(',')[0]}
                </div>
              </div>

              {/* Date & Time */}
              <div className="mb-3">
                <div className="flex items-center text-xs font-medium text-gray-600 mb-1">
                  <Clock size={10} className="mr-1" />
                  {formatDate(slot.date)}
                </div>
                <div className="text-lg font-bold text-green-600">
                  {slot.time}
                </div>
              </div>

              {/* Available Spots */}
              <div className="flex items-center justify-between">
                <div className="flex items-center text-xs text-green-600">
                  <Users size={12} className="mr-1" />
                  {slot.availableSpots === slot.maxPlayers
                    ? `${slot.maxPlayers} spots`
                    : `${slot.availableSpots}-${slot.maxPlayers} spots`
                  }
                </div>
                {slot.price && (
                  <div className="text-xs font-semibold text-gray-800">
                    ${slot.price}
                  </div>
                )}
              </div>
            </div>
          </Link>
        ))}
      </div>

      {/* Status Text */}
      <div className="max-w-6xl mx-auto px-4 md:px-6 text-center mt-6 mb-6">
        <p className="text-sm text-gray-500">
          Live availability across multiple courses
        </p>
      </div>

      {/* CTA Section */}
      <div className="max-w-6xl mx-auto px-4 md:px-6 text-center">
        <Link
          to="/tee-times"
          className="inline-flex items-center px-6 py-3 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 transition-colors shadow-md hover:shadow-lg"
        >
          View All Available Tee Times
          <ArrowRight className="ml-2" size={20} />
        </Link>
      </div>

      {/* Error State */}
      {error && (
        <div className="max-w-6xl mx-auto px-4 md:px-6 mt-4">
          <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <p className="text-sm text-yellow-800">
              {error} • Showing course directory
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default LiveTeeTimesScroller;